import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { euro, fmtWeight, methodBlockReason } from "@/lib/shop";
import { AddressFields, addressComplete } from "@/components/AddressFields";
import { Spinner } from "@/components/ProtectedRoute";
import { useCart } from "@/context/CartContext";

const ShippingOption = ({ m, reason, selected, onSelect }) => (
  <label
    data-testid={`shipping-option-${m.id}`}
    data-blocked={reason ? "true" : "false"}
    className={`flex items-center gap-3 rounded-xl border-2 p-3 transition-colors duration-150 ${
      reason ? "cursor-not-allowed border-border bg-muted/50 opacity-60" : selected ? "cursor-pointer border-[#0b0b0f] bg-[#fff6c2]" : "cursor-pointer border-border bg-white hover:border-[#0b0b0f]"
    }`}
  >
    <input type="radio" name="shipping" className="h-4 w-4 accent-black" disabled={!!reason} checked={selected} onChange={onSelect} data-testid={`shipping-radio-${m.id}`} />
    <div className="flex-1">
      <div className="text-sm font-bold">{m.name}</div>
      <div className="text-xs text-muted-foreground">{reason || `${m.description} · max ${fmtWeight(m.max_weight_g)}`}</div>
    </div>
    <div className="font-display font-extrabold">{euro(m.cost)}</div>
  </label>
);

export default function CheckoutPage() {
  const { items, subtotal, weight } = useCart();
  const [address, setAddress] = useState(null);
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState(null);
  const [save, setSave] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setAddress({ ...data, country: data.country || "IT" }));
    api.get("/shipping").then(({ data }) => setMethods(data));
  }, []);

  const country = address?.country || "IT";
  const selected = methods.find((m) => m.id === method && !methodBlockReason(m, weight, country));
  useEffect(() => {
    if (!selected) setMethod(methods.find((m) => !methodBlockReason(m, weight, country))?.id || null);
  }, [methods, weight, country]); // eslint-disable-line react-hooks/exhaustive-deps

  if (items.length === 0) return <Navigate to="/carrello" replace />;
  if (!address) return <Spinner />;

  const total = subtotal + (selected?.cost || 0);
  const pay = async () => {
    if (!addressComplete(address)) return toast.error("Compila tutti i campi dell'indirizzo");
    if (!selected) return toast.error("Nessun metodo di spedizione disponibile");
    setPaying(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping_method: selected.id,
        address: { full_name: address.full_name, phone: address.phone, address: address.address, city: address.city, zip: address.zip, province: address.province || "", country },
        save_to_profile: save,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "Pagamento non avviato"));
      setPaying(false);
    }
  };

  return (
    <div className="mx-auto grid max-w-5xl gap-6 px-4 pt-6 sm:px-6 md:grid-cols-[1fr_340px]">
      <div className="space-y-6">
        <h1 className="text-3xl font-extrabold tracking-tight">Cassa</h1>
        <section className="card-mc p-4">
          <h2 className="mb-3 text-base font-extrabold md:text-lg">Indirizzo di spedizione</h2>
          <AddressFields value={address} onChange={setAddress} prefix="checkout" />
          <label className="mt-3 flex items-center gap-2 text-sm">
            <Checkbox data-testid="checkout-save-profile" checked={save} onCheckedChange={(v) => setSave(!!v)} />
            Salva questo indirizzo nel profilo
          </label>
        </section>
        <section className="card-mc p-4">
          <h2 className="mb-1 text-base font-extrabold md:text-lg">Spedizione</h2>
          <p className="mb-3 text-xs text-muted-foreground">Peso del pacco: <b data-testid="checkout-weight">{fmtWeight(weight)}</b></p>
          <div className="space-y-2">
            {methods.map((m) => {
              const reason = methodBlockReason(m, weight, country);
              return <ShippingOption key={m.id} m={m} reason={reason} selected={selected?.id === m.id} onSelect={() => setMethod(m.id)} />;
            })}
          </div>
        </section>
      </div>
      <aside className="md:pt-14">
        <div className="card-mc sticky top-24 p-4">
          <h2 className="text-base font-extrabold md:text-lg">Riepilogo</h2>
          <div className="mt-3 space-y-1.5 text-sm">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between gap-2"><span className="truncate">{i.quantity}× {i.name}</span><span>{euro(i.price * i.quantity)}</span></div>
            ))}
            <div className="flex justify-between border-t border-dashed border-border pt-2"><span>Spedizione</span><span data-testid="checkout-shipping-cost">{selected ? euro(selected.cost) : "—"}</span></div>
            <div className="flex justify-between font-display text-xl font-black"><span>Totale</span><span data-testid="checkout-total">{euro(total)}</span></div>
          </div>
          <button data-testid="checkout-submit-button" className="btn-mc mt-4 w-full !py-3" disabled={paying || !selected} onClick={pay}>
            {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-4 w-4" />} Paga con carta
          </button>
          <p className="mt-2 text-center text-xs text-muted-foreground">Pagamento sicuro con Stripe</p>
        </div>
      </aside>
    </div>
  );
}
